import { GenericResponse } from 'src/js/model/api/response/generic-response';
import { ipv6Regex } from 'src/js/validator/ip-validator-helper';
import { AudioAlgorithmEnum, AudioEncoderMode } from '../audio-encoder/audio-encoder-models';
import { TFunction } from 'i18next';
import { formatBytesWithUnits } from './stream-view-model-mapper';

export enum StreamEncapsulation {
  UDP = 2,
  RTP = 3,
  SRT = 34,
  WHIP = 128,
}

export enum StreamEncapsulationWithoutSRT {
  UDP = 2,
  RTP = 3,
}

export enum SRTMode {
  CALLER = 0,
  LISTENER = 1,
  RENDEZVOUS = 2,
}

export enum SRTEncryption {
  SRT_ENCRYPTION_NONE = 0,
  SRT_ENCRYPTION_128BIT = 16,
  SRT_ENCRYPTION_256BIT = 32,
}

export enum StreamShaping {
  VBR = 'vbr',
  CVBR = 'cvbr',
  CBR = 'cbr',
}

export enum SRTAuthentication {
  NONE = 'none',
  AUTO = 'auto',
  AES_GCM = 'AES-GCM',
}
export enum SRTAuthenticationListener {
  AUTO = 'auto',
  AES_GCM = 'AES-GCM',
}
export enum SRTAuthenticationOthers {
  NONE = 'none',
  AES_GCM = 'AES-GCM',
}

export enum NetworkInterfaces {
  auto = 'auto',
  eth0 = 'eth0',
  eth1 = 'eth1',
}

export enum StreamPublishingMode {
  standard = 'standard',
  custom = 'custom',
}

export enum FecRTP {
  DISABLED = 0,
  MPEG_PRO_FEC = 2,
}

export enum MpegProFecLevel {
  COLUMNS_ONLY = 0,
  ROW_AND_COLUMNS = 1,
}

export enum StreamState {
  STOPPED = 0,
  RUNNING,
  FAILED,
  CONNECTING,
  SECURING /* Acquiring keys for protected content */,
  LISTENING,
  PAUSED,
  PUBLISHING /* For RTMP after connection is established */,
  RESOLVING /* DNS resolution of host name to IP address in progress */,
  SCRAMBLED /* Stream encrypted and cannot decrypt */,
  CONNECTED /* Steady state for backup link of Active-Backup SRT redundant stream */,
  INITIALIZING /* used in WHIP */,
}

export enum SrtPathRedundancyMode {
  NONE = 'none',
  OPTIONAL = 'optional',
  ACTIVE_ACTIVE = 'active',
  ACTIVE_BACKUP = 'backup',
}

export enum SrtPathRedundancyModeOffered {
  NONE = 'none',
  ACTIVE_ACTIVE = 'active',
  ACTIVE_BACKUP = 'backup',
}

export enum SrtRedundancyPathRole {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
}

export enum MetadataEncapsulation {
  SYNC = 0 /* Sync Metadata (AU header) in Metadata PES (ISO/IEC 13818-1), default  */,
  ASYNC /* Async Metadata in Private Data PES (MISB 6004.2) */,
  ASYNC_SYNC_AU /* Sync AU in Async Metadata PES&PMT */,
}

export enum RecordFormat {
  TS = 'ts',
  MP4 = 'mp4',
}

export enum RecordLimitType {
  NONE = 'none',
  TIME = 'time',
  SIZE = 'size',
}

export enum RecordLimitSizeUnit {
  GB = 'GB',
  MB = 'MB',
}

// converts a recording limit/segment size + unit (GB/MB) entered in the UI into KBytes, matching the backend unit
export const convertRecordSizeToKBytes = (size?: number | string, unit?: RecordLimitSizeUnit): number => {
  const numericSize = Number(size);
  if (!size || isNaN(numericSize)) {
    return 0;
  }
  if (unit === RecordLimitSizeUnit.GB) {
    return numericSize * 1024 * 1024;
  }
  return numericSize * 1024; // MB
};

// converts kbytes back into the given unit (GB/MB) for display purposes, rounded up so the value is a valid minimum
export const convertKBytesToSize = (sizeInKBytes: number, unit?: RecordLimitSizeUnit): number => {
  if (unit === RecordLimitSizeUnit.GB) {
    return Math.ceil(sizeInKBytes / (1024 * 1024));
  }
  return Math.ceil(sizeInKBytes / 1024); // MB
};

interface SRTStats {
  pathName?: string;
  state: StreamState;
  srtRedundancyMode: SrtPathRedundancyMode;
  detectedRedundancyMode: SrtPathRedundancyMode;
  resentPackets: string;
  resentBytes: string;
  droppedPackets: string;
  droppedBytes: string;
  latency: number;
  localPort: number;
  remotePort: number;
  maxBandwidth: string;
  pathMaxBandwidth: string;
  lostPackets: string;
  localVersion: string;
  peerVersion: string;
  localEndpoint: string;
  peerEndpoint: string;
  buffer: number;
  reconnections: string;
  remoteAddress: string;
  mss: number;
  rtt: number;
  rrtWithUnits: string /* view model only */;
  recvACK: string;
  recvNAK: string;
  encryption?: string;
  encryptionMode?: string;
  keyLength?: string;
  authentication?: string;
  peerDecryption?: string;
  decryptError?: string;
  rejectedLastTime: string;
  rejectedReason: string;
  rejectedStreamId: string;
  path2: {
    pathName?: string;
    state: StreamState;
    reconnections: string;
    resentPackets: string;
    resentBytes: string;
    droppedPackets: string;
    droppedBytes: string;
    recoveredPackets: string;
    lostPackets: string;
    localVersion: string;
    peerVersion: string;
    localEndpoint: string;
    peerEndpoint: string;
    recvACK: string;
    recvNAK: string;
    mss: number;
    maxBandwidth: string;
    pathMaxBandwidth: string;
    sourceAddress: string;
    remoteAddress: string;
    localAddress: string;
    peerAddress: string;
    localPort: number;
    peerPort: number;
    remotePort: number;
    rtt: number;
    rrtWithUnits: string /* view model only */;
    buffer: number;
    latency: number;
    encryption?: string;
    encryptionMode?: string;
    keyLength?: string;
    authentication?: string;
    peerDecryption?: string;
    decryptError?: string;
  };
}

export interface StreamRecording {
  autoStart?: boolean;
  started?: boolean; // useful when editing a stream with autoStart=false but we want it to be created started right away.
  enabled?: boolean;
  id?: number;
  name?: string;
  format?: RecordFormat; // current code emits "ts", keep mp4 for forward compatibility
  status?: string;
  videoSamples?: string;
  audioSamples?: string;
  bytesStored?: string;
  bytesStoredFormatted?: string;
  limited?: boolean;
  recycleSegments?: boolean;
  segmented?: boolean;
  segmentType?: RecordLimitType;
  upTime?: string;
  upTimeClock?: string;

  queueDepth?: string;
  peakQueueDepth?: string;
  queueOverflows?: string;
  lastQueueOverflow?: string;

  // present when limited === true
  limitType?: RecordLimitType;
  limitSize?: string;
  limitSizeFromApi?: string; // in KBytes
  percentageDone?: number;

  // present when segmented === true
  segmentSize?: string;
}

// pure UI construct
export interface StreamRecordingViewModel extends StreamRecording {
  limitSizeHour?: number;
  limitSizeMinutes?: number;
  limitSizeUnit?: RecordLimitSizeUnit;

  // segmentSize?: number|string;
  segmentSizeUnit?: RecordLimitSizeUnit;
  segmentHour?: number;
  segmentMinutes?: number;
  filename?: string;
}

export const recordIsRunning = (record?: StreamRecording): boolean => {
  return record?.status === 'RECORDING' || record?.status === 'INITIALIZING';
};

export interface StreamInfo {
  id: number;
  decoderId?: number;
  name?: string;
  type?: number; // unused
  bandwithEstimate?: number | string;
  encapsulation: StreamEncapsulation;
  metadataEncapsulation?: MetadataEncapsulation;
  metadataBandwidth?: number | string;
  metadataBandwidthAutoAssigned?: boolean;
  idleCells: boolean;
  delayedAudio: boolean;
  persistent: boolean;
  fec?: number; // unused
  mtu: number;
  tos: string;
  ttl: number;
  port: number | '';
  address?: string;
  url?: string; // for WHIP
  bearerToken?: string; // for WHIP
  sourceIp?: string;
  stillImage?: string;
  userData?: string;
  shaping?: StreamShaping;
  sourcePort?: number | '';
  strictMode?: boolean;
  networkInterface?: string;
  overheadPercentage?: number;
  authentication?: SRTAuthentication;
  recording?: StreamRecording;

  // srt
  srtMode?: SRTMode;
  adaptive?: boolean; // Dynamic Bitrate Control (SRT only)
  encryption?: boolean;
  encKeyLength?: number;
  latency?: number;
  passphrase?: string;
  passphraseSet?: boolean;
  srtAccessControlPublishingIdType?: string;
  srtAccessPublishingID?: string;
  srtAccessUserName?: string;
  srtAccessResourceName?: string;
  srtListenerSecondPort?: number | '';
  srtRedundancyMode?: SrtPathRedundancyMode;
  srtRedundancyPath1Role?: string;
  srtRedundancyPath1Address?: string;
  srtRedundancyPath2Address?: string;
  srtRedundancyPath1Port?: number | '';
  srtRedundancyPath2Port?: number | '';
  srtRedundancyPath1SourcePort?: number | '';
  srtRedundancyPath2SourcePort?: number | '';
  srtRedundancyPath1Name?: string;
  srtRedundancyPath2Name?: string;
  srtRedundancyPath2NetworkInterface?: string;

  fecRtp?: FecRTP;
  level?: MpegProFecLevel;
  rows?: number;
  columns?: number;
  blockAligned?: boolean;
  sap?: {
    id?: number;
    advertise?: boolean;
    name?: string;
    desc?: string;
    author?: string;
    keywords?: string;
    copyright?: string;
    addressConfigured?: string;
    address?: string;
    port?: number;
  };
  audio?: {
    autoassigned?: boolean;
    channels?: number;
    codec?: AudioAlgorithmEnum;
    id: number;
    interface?: number;
    layout?: AudioEncoderMode;
    name?: string;
    pid?: number;
  }[];
  video?: { id: number; name?: string; autoassigned?: boolean; pid?: number; interface?: number }[];
  metadata?: {
    id: number;
    name?: string;
    autoassigned?: boolean;
    pid?: number;
    interface?: number;
  }[];
  scte?: { id: number; name?: string; autoassigned?: boolean; pid?: number; interface?: number }[];
  pcrPid?: number;
  pmtPid?: number;
  tsStreamId?: number;
  programNumber?: number;
  pmtPidAutoAssigned?: boolean;
  pcrPidAutoAssigned?: boolean;
  programNumberAutoAssigned?: boolean;
}

export interface StreamStats {
  state: StreamState;
  stateString: string;
  uptime: string;
  lastReset: string;
  sentPackets: string;
  sentBytes: string;
  bitrate: string;
  bitrateInKbps: number;
  destAddress?: string;
  duplicate?: string;
  rtmpLastError?: string;
  ssrc?: string;
  sourcePort?: number;
  unsentPackets?: string;
  unsentBytes?: string;
  lastError?: string;
  occurred?: string;
  /* rtcp only begins */
  rtcp?: string;
  rxOKRTCPPackets?: string;
  rxOKRTCPBytes?: string;
  txOKRTCPPackets?: string;
  txOKRTCPBytes?: string;
  RTCPReporters?: number;
  rxInvalidRTCPPackets?: string;
  rxInvalidRTCPBytes?: string;
  lastRxError?: string;
  txFailedRTCPPackets?: string;
  txFailedRTCPBytes?: string;
  lastTxError?: string;
  /* rtcp only ends */

  programNumber?: string;
  pcrPid?: string;
  sourceAddress?: string;
  connections?: string;
  fecRtp?: number;
  fecLevel?: string;
  fecRows?: string;
  fecColumns?: string;
  droppedPackets?: string;
  lostPackets?: string;
  corruptedFrames?: number;
  restarts?: string;
  srt?: SRTStats;
  sources?: [
    {
      name: string;
      compression: string;
      bitrate: string;
      programId: number;
      receivedPackets: string;
      receivedBytes: string;
      PTS: string;
      DTS: string;
    },
  ];
  whipLocalPort?: number;
  whipRemotePort?: number;
  whipFullIntraRequests?: string;
  whipPictureLoss?: string;
  whipNACK?: string;
  whipPacketLoss?: string;
  whipRTT?: string;
  whipJitter?: string;
  whipLastError?: string;
  whipLastErrorOccured?: string;
}

export interface StreamResponse extends GenericResponse {
  info: StreamInfo;
  stats: StreamStats;
}

export interface GetAllStreamsResponse {
  data: StreamResponse[];
}

export interface StreamViewModel extends StreamInfo {
  audioPids: string;
  videoPids: string;
  metadataPids: string;
  sctePids: string;
  audioPidsPlaceholder?: string;
  videoPidsPlaceholder?: string;
  metadataPidsPlaceholder?: string;
  sctePidsPlaceholder?: string;
  pcrPidPlaceholder?: string;
  pmtPidPlaceholder?: string;
  programNumberPlaceholder?: string;
  pcrPid?: number;
  pmtPid?: number;
  tsStreamId?: number;
  programNumber?: number;
  tsStreamIdPlaceholder?: string;
  metadataBandwidthPlaceholder?: string;
  sapAddressPlaceholder?: string;

  destination: string;
  encrypted: boolean;
  stateString: string;
  statusIcon: string;
  stats: Partial<StreamStats>;
  encapsulationTextual: string;

  videoOutputResolution: string;
  videoBitrate: string;
  audioBitrate: string;

  // record stuff
  recording?: StreamRecordingViewModel;
  /*
  recordAutoStart?: boolean;
  recordName?: string;
  recordFormat?: RecordFormat;
  recordLimitType?: RecordLimitType;
  recordLimitSize?: number;
  recordLimitSizeUnit?: RecordLimitSizeUnit;
  recordLimitTimeHour?: number;
  recordLimitTimeMinutes?: number;
  recordSegmentation?: RecordLimitType;
  recordSegmentationSizeUnit?: RecordLimitSizeUnit;
  recordSegmentationSize?: number;
  recordSegmentationHour?: number; // puer UI construct
  recordSegmentationMinutes?: number; // puer UI construct*/

  codecAlgorithm?: string; // comes from videoEncoder
  previewIntervalSec: number;
  srtStreamId: boolean; // pure UI helper variable
  stateSortable: number;
  state: StreamState;
  selected: boolean /* checked by checkbox */;
  hidden: boolean /* when filtered out */;
  updated: string /* to trigger setState to detect change and re-render */;
}

export interface RecordModel {
  autoStart?: boolean;
  format?: RecordFormat;
  limitType?: RecordLimitType;
  limitSize?: string | string;
  limitTime?: number;
  name?: string;
  recycleSegments?: boolean;
  segmentType?: RecordLimitType;
  segmentSize: number | string;
  streamId?: number;
}

export interface RecordResponse {
  enabled?: boolean;
  id: number; // streamId
  name: string;
  format: 'ts' | 'mp4' | string;
  state?: 'started' | 'stopped' | 'initial' | 'recording' | string;
  limitType?: 'time' | 'size' | 'none';
  limitSize?: number; // seconds or KB depending on limitType
  segmentType?: 'time' | 'size' | 'none';
  segmentSize?: number; // seconds or KB depending on segmentType
  segmented?: boolean;
  recycleSegments?: boolean;
  bAutoStart?: boolean;
  bAutoExport?: boolean;
  bDeleteAfterExport?: boolean;
  nExportDestID?: number;
}

export interface GetSingleRecordingsResponse extends GenericResponse {
  recording: RecordResponse;
}

export interface GetAllRecordingsResponse extends GenericResponse {
  data: RecordResponse[];
}

// stream to record for form submit
export const mapStreamModelToRecordModel = (model: StreamViewModel): RecordModel => {
  let limitSize: number | undefined = undefined;
  /*console.log(
    'prepare submit',
    model.recording.limitType,
    model.recording.limitSizeHour,
    model.recording.limitSizeMinutes,
  );*/
  if (model.recording.limitType === RecordLimitType.TIME) {
    // time
    limitSize = (model.recording.limitSizeHour ?? 0) * 3600 + (model.recording.limitSizeMinutes ?? 0) * 60;
  } else if (model.recording.limitType === RecordLimitType.SIZE) {
    // size
    if (model.recording.limitSizeUnit === RecordLimitSizeUnit.GB) {
      // console.log('limitSize',model.recording.limitSize, Number(model.recording.limitSize))
      limitSize = model.recording.limitSize ? Number(model.recording.limitSize) * 1024 * 1024 : undefined; // convert to MB since backend expects size in KB
    } else {
      limitSize = model.recording.limitSize ? Number(model.recording.limitSize) * 1024 : undefined;
    }
  }

  let segmentSize: number | undefined = undefined;
  if (model.recording.segmentType === RecordLimitType.TIME) {
    // time
    segmentSize = (model.recording.segmentHour ?? 0) * 3600 + (model.recording.segmentMinutes ?? 0) * 60;
  } else if (model.recording.segmentType === RecordLimitType.SIZE) {
    // size
    if (model.recording.segmentSizeUnit === RecordLimitSizeUnit.GB) {
      segmentSize = model.recording.segmentSize ? Number(model.recording.segmentSize) * 1024 * 1024 : undefined; // convert to MB since backend expects size in KB
    } else {
      segmentSize = model.recording.segmentSize ? Number(model.recording.segmentSize) * 1024 : undefined;
    }
  }

  limitSize = Math.floor(limitSize);
  segmentSize = Math.floor(segmentSize);

  // prepare values for sending to API
  return {
    autoStart: model.recording.autoStart,
    name: model.recording.name,
    format: model.recording.format,
    limitType: model.recording.limitType,
    limitSize: model.recording.limitType === RecordLimitType.NONE ? undefined : limitSize?.toString(),
    recycleSegments: model.recording.recycleSegments,
    segmentType: model.recording.segmentType,
    segmentSize: model.recording.segmentType === RecordLimitType.NONE ? undefined : segmentSize?.toString(),
    streamId: model.id,
  };
};

export const formatHourMinute = (limitSizeHour: string, limitSizeMinutes: string) => {
  if (limitSizeHour && limitSizeHour.length > 0) {
    return `${limitSizeHour}h${limitSizeMinutes}m`;
  }
  return `${limitSizeMinutes}m`;
};

export const getRecordingTooltip = (stream: StreamViewModel, t: TFunction) => {
  if (stream.recording == null) {
    return undefined;
  }
  const record = stream.recording;
  if (streamIsStopped(stream.state) && record.autoStart) {
    return undefined;
  }
  if (record.status == 'READY') {
    return undefined;
  }
  const formatted = formatBytesWithUnits(record.bytesStored, 2, false);
  if (record.status == 'FINISHED' || record.status == 'STOPPED') {
    return `${formatted.string}`;
  }
  if (record.limitType === RecordLimitType.TIME) {
    if (record.upTime) {
      let totalTime = formatHourMinute(record.limitSizeHour?.toString(), record.limitSizeMinutes?.toString());

      if (totalTime.length >= 4 && totalTime.endsWith('h0m')) {
        totalTime = totalTime.substring(0, totalTime.length - 2);
      }
      if (totalTime.length >= 3 && totalTime.startsWith('0h')) {
        totalTime = totalTime.substring(2);
      }
      if (record.segmented && record.recycleSegments) {
        // no limit
        return `${formatted.string}`;
      }
      return `${record.upTime} ${t('general.of')} ${totalTime}`;
    }
    return `${formatted.string}`; // this happen during disk full, uptime is ""
  } else if (record.limitSize && record.limitType === RecordLimitType.SIZE) {
    // TODO if we want same format for both limit and current const limitSize = formatBytesWithUnits(record.limitSize, 2, true);

    if (formatted.unit === record.limitSizeUnit) {
      if (record.segmented && record.recycleSegments) {
        // segmented and recycling, overall limit by size
        return `${formatted.string}`;
      }
      return `${formatted.size} ${t('general.of')} ${record.limitSize} ${record.limitSizeUnit}`;
    }

    if (record.segmented && record.recycleSegments) {
      // segmented and recycling, overall limit by time
      return `${formatted.string}`;
    }
    return `${formatted.string} ${t('general.of')} ${record.limitSize} ${record.limitSizeUnit}`;
  }

  return `${formatted.string}`; // bytes stored
};

export const getStreamUuid = (model: StreamViewModel | StreamInfo) => {
  if (model.userData?.length > 0) {
    return model.userData;
  }
  return model.id; // for old streams or streams created by CLI and other interfaces
};

export const streamIsStopped = (state: StreamState): boolean => state === StreamState.STOPPED;

export const streamIsStarted = (state: StreamState): boolean => state !== StreamState.STOPPED;

const getDestinationShorthandRightSide = (model: StreamViewModel) => {
  if (
    model.stats != null &&
    (model.srtRedundancyMode === 'active' || model.srtRedundancyMode === 'backup') && // active-active or active-backup
    Number(model.srtRedundancyPath2Port) > 0 &&
    model.stats.srt != null &&
    model.stats.srt.state !== StreamState.RUNNING /*NOT STREAMING*/ &&
    model.stats.srt.path2 != null &&
    model.stats.srt.path2.state === StreamState.RUNNING /*STREAMING*/
  ) {
    // feed from path2 since this one is connected and path1 is not
    return ':' + model.srtRedundancyPath2Port;
  } else if (model.port > 0 && model.srtRedundancyMode === 'optional' && Number(model.srtListenerSecondPort) > 0) {
    return ':' + model.port + ', ' + model.srtListenerSecondPort;
  } else if (model.port > 0) {
    return ':' + model.port;
  }
  return '';
};

export const getDestinationShorthand = (model: StreamViewModel) => {
  let address = model.address;
  if (model.encapsulation === StreamEncapsulation.WHIP) {
    address = model.url;
  }

  if (
    model.stats != null &&
    (model.srtRedundancyMode === 'active' || model.srtRedundancyMode === 'backup') && // active-active or active-backup
    Number(model.srtRedundancyPath2Port) > 0 &&
    model.stats.srt != null &&
    model.stats.srt.state !== StreamState.RUNNING /*NOT STREAMING*/ &&
    model.stats.srt.path2 != null &&
    model.stats.srt.path2.state === StreamState.RUNNING /*STREAMING*/
  ) {
    // feed from path2 since this one is active
    address = model.srtRedundancyPath2Address;
  } else if (
    (address === 'Any' || address === '0.0.0.0') &&
    model.stats != null &&
    model.stats.srt != null &&
    model.stats.srt.remoteAddress != null &&
    model.stats.srt.remoteAddress.length > 0
  ) {
    address = model.stats.srt.remoteAddress;
  }

  if (
    (address === 'Any' || address === '0.0.0.0') &&
    model.stats != null &&
    model.stats.srt != null &&
    model.stats.srt.remoteAddress != null &&
    (model.stats.srt.remoteAddress === 'Any' || model.stats.srt.remoteAddress === '0.0.0.0') &&
    model.srtRedundancyMode === 'optional' && // listener mode.
    model.stats.srt.path2 != null &&
    model.stats.srt.path2.remoteAddress != null &&
    model.stats.srt.path2.remoteAddress !== 'Any' &&
    model.stats.srt.path2.remoteAddress !== '0.0.0.0' &&
    model.stats.srt.path2.remoteAddress.length > 0
  ) {
    address = model.stats.srt.path2.remoteAddress; // feed from path2 if path1 has not remote address
  }

  if (address && ipv6Regex.exec(address)) {
    address = '[' + address + ']';
  }
  return address + getDestinationShorthandRightSide(model);
};

export const isEmptyContentSource = (source: any) => {
  if (source == null) {
    return true;
  }
  if (Array.isArray(source) && source.length === 0) {
    return true;
  }
  if (Array.isArray(source) && source.length === 1 && source[0].id == -1) {
    return true;
  }
  return false;
};
